import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  makeStyles,
  useTheme
} from '@material-ui/core';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 12,
      minWidth: 400,
    }
  },  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(1),
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  content: {
    paddingTop: theme.spacing(1),
  },
  messagePreview: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  originalMessage: {
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    marginBottom: theme.spacing(1),
  },  timeWarning: {
    color: theme.palette.warning.main,
    fontSize: '0.85rem',
    marginTop: theme.spacing(1),
    fontStyle: 'italic',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.warning.light + '20',
    borderRadius: 4,
    border: `1px solid ${theme.palette.warning.light}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  timeExpired: {
    color: theme.palette.error.main,
    backgroundColor: theme.palette.error.light + '20',
    border: `1px solid ${theme.palette.error.light}`,
  },  timeUrgent: {
    color: theme.palette.error.main,
    backgroundColor: theme.palette.error.light + '10',
    border: `1px solid ${theme.palette.error.light}`,
    animation: '$pulse 1.5s ease-in-out infinite',
    fontWeight: 'bold',
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
      transform: 'scale(1)',
    },
    '50%': {
      opacity: 0.8,
      transform: 'scale(1.02)',
    },
    '100%': {
      opacity: 1,
      transform: 'scale(1)',
    },
  },
  deleteWarning: {
    color: theme.palette.error.main,
    backgroundColor: theme.palette.error.light + '20',
    padding: theme.spacing(2),
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
  editField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.paper,
    }
  }
}));

const MessageEditDeleteModal = ({
  open,
  onClose,
  message,
  onEdit,
  onDelete,
  mode = 'edit', // 'edit' ou 'delete'
  loading = false
}) => {
  const classes = useStyles();
  const theme = useTheme();  const [editedMessage, setEditedMessage] = useState(message?.message || '');
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  // ✅ Sincronizar estado quando a mensagem muda
  useEffect(() => {
    if (message?.message) {
      setEditedMessage(message.message);
      setError('');
    }
  }, [message]);
  // ✅ Contador regressivo em tempo real
  useEffect(() => {
    if (!open || !message) return;

    const interval = setInterval(() => {
      setCurrentTime(new Date());
      
      // ✅ Fechar modal automaticamente quando o tempo esgotar
      const messageTime = new Date(message.createdAt);
      const now = new Date();
      const diffMinutes = (now - messageTime) / (1000 * 60);
      
      if (diffMinutes >= 5) {
        // Tempo esgotado, fechar modal em 3 segundos
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    }, 1000); // Atualiza a cada segundo

    return () => clearInterval(interval);
  }, [open, message, onClose]);

  // Calcular tempo restante para edição/exclusão (5 minutos)
  const getTimeRemaining = () => {
    if (!message) return 0;
    
    const messageTime = new Date(message.createdAt);
    const diffMinutes = (currentTime - messageTime) / (1000 * 60);
    const remaining = 5 - diffMinutes;
    
    return Math.max(0, remaining);
  };

  const timeRemaining = getTimeRemaining();
  const canEditOrDelete = timeRemaining > 0;

  const handleEdit = () => {
    if (!editedMessage.trim()) {
      setError('A mensagem não pode estar vazia');
      return;
    }

    if (editedMessage.trim() === message.message.trim()) {
      setError('A mensagem não foi alterada');
      return;
    }

    onEdit(message.id, editedMessage.trim());
  };

  const handleDelete = () => {
    onDelete(message.id);
  };
  const formatTimeRemaining = (minutes) => {
    if (minutes <= 0) return { text: 'Tempo esgotado', expired: true };
    
    const mins = Math.floor(minutes);
    const secs = Math.floor((minutes - mins) * 60);
    
    const timeText = `${mins}:${secs.toString().padStart(2, '0')}`;
    const isUrgent = minutes < 1; // Menos de 1 minuto
    
    return { 
      text: timeText, 
      expired: false, 
      urgent: isUrgent,
      icon: isUrgent ? '⚠️' : '⏱️'
    };
  };

  if (!message) return null;

  return (    <Dialog 
      open={open} 
      onClose={onClose}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className={classes.header}>
        <Box display="flex" alignItems="center" gap={1}>
          {mode === 'edit' ? <EditIcon color="primary" /> : <DeleteIcon color="error" />}
          <Typography variant="h6">
            {mode === 'edit' ? 'Editar Mensagem' : 'Deletar Mensagem'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>      <DialogContent className={classes.content}>
        {/* Aviso de tempo */}
        {(() => {
          const timeInfo = formatTimeRemaining(timeRemaining);
          const timeClass = `${classes.timeWarning} ${timeInfo.expired ? classes.timeExpired : ''} ${timeInfo.urgent ? classes.timeUrgent : ''}`;
          
          return (
            <Typography className={timeClass}>
              <span>{timeInfo.icon}</span>
              {timeInfo.expired ? (
                <span>
                  <strong>Tempo esgotado!</strong> Mensagens só podem ser {mode === 'edit' ? 'editadas' : 'deletadas'} até 5 minutos após o envio.
                </span>
              ) : (
                <span>
                  <strong>{timeInfo.text}</strong> restante para {mode === 'edit' ? 'editar' : 'deletar'} esta mensagem
                </span>
              )}
            </Typography>
          );
        })()}

        {/* Mensagem original */}
        <Box className={classes.messagePreview}>
          <Typography variant="caption" className={classes.originalMessage}>
            Mensagem original:
          </Typography>
          <Typography>
            {message.message}
          </Typography>
        </Box>

        {mode === 'edit' && canEditOrDelete && (
          <>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={editedMessage}
              onChange={(e) => {
                setEditedMessage(e.target.value);
                setError('');
              }}
              placeholder="Digite sua mensagem..."
              variant="outlined"
              className={classes.editField}
              disabled={loading}
              error={!!error}
              helperText={error}
            />
          </>
        )}

        {mode === 'delete' && canEditOrDelete && (
          <Box className={classes.deleteWarning}>
            <Typography variant="body2">
              ⚠️ <strong>Atenção:</strong> Esta ação não pode ser desfeita. A mensagem será removida permanentemente para todos os participantes do chat.
            </Typography>
          </Box>
        )}

        {!canEditOrDelete && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
            As mensagens só podem ser {mode === 'edit' ? 'editadas' : 'deletadas'} dentro de 5 minutos após o envio para garantir a integridade das conversas.
          </Typography>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px' }}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        
        {canEditOrDelete && (
          <Button
            onClick={mode === 'edit' ? handleEdit : handleDelete}
            color={mode === 'edit' ? 'primary' : 'secondary'}
            variant="contained"
            disabled={loading || (mode === 'edit' && !editedMessage.trim())}
            style={{
              backgroundColor: mode === 'delete' ? theme.palette.error.main : undefined,
              color: mode === 'delete' ? theme.palette.error.contrastText : undefined,
            }}
          >
            {loading ? 'Processando...' : (mode === 'edit' ? 'Salvar' : 'Deletar')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MessageEditDeleteModal;
